import React from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { AppModal } from '../../../components/AppModal';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { SETTINGS_BTN_PRIMARY, SETTINGS_FIELD_LABEL, SETTINGS_INPUT_CLASS } from '../utils/settingsSectionStyles';

export type MeinshkEditSettings = { clientId: string; hasClientPassword: boolean };
export type MeinshkSaveResult = { testedOk: boolean; testError?: string | null };

function RequiredMark() {
  return <span className="text-red-500"> *</span>;
}

export function MeinshkConnectModal({
  open,
  editSettings,
  onClose,
  onSaved,
}: {
  open: boolean;
  editSettings: MeinshkEditSettings | null;
  onClose: () => void;
  onSaved: (result: MeinshkSaveResult) => void;
}) {
  const [clientId, setClientId] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [visible, setVisible] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const firstRef = React.useRef<HTMLInputElement>(null);

  const isEdit = Boolean(editSettings);
  const hasStoredPassword = Boolean(editSettings?.hasClientPassword);

  React.useEffect(() => {
    if (!open) return;
    setClientId(editSettings?.clientId ?? '');
    setPassword('');
    setVisible(false);
    setSaving(false);
    setError(null);
    const t = window.setTimeout(() => firstRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open, editSettings]);

  useEscapeClose(onClose, open && !saving);

  const save = async () => {
    if (!/^\d+$/.test(clientId.trim())) {
      setError('Die Client-ID ist eine Zahl (Mandantennummer bei MeinSHK).');
      return;
    }
    if (!password.trim() && !hasStoredPassword) {
      setError('Bitte das API-Passwort eintragen.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/settings/meinshk', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId: clientId.trim(), clientPassword: password }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Speichern fehlgeschlagen.');
      onSaved({ testedOk: Boolean(d?.testedOk), testError: d?.testError ?? null });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      closeOnOverlayClick={false}
      disableClose={saving}
      overlayClassName="z-50"
      panelClassName="md:max-w-lg md:h-auto"
      ariaLabelledBy="meinshk-connect-title"
    >
      <div className="flex flex-col flex-1 min-h-0 h-full">
        <div className="px-5 py-3 border-b border-app-border flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 id="meinshk-connect-title" className="text-sm font-semibold text-app-text">
              {isEdit ? 'MeinSHK-Zugang bearbeiten' : 'MeinSHK verbinden'}
            </h2>
            <p className="text-xs text-app-text-subtle mt-0.5">
              {isEdit
                ? 'Zugangsdaten ändern und erneut prüfen. Ein leeres Passwortfeld bleibt unverändert.'
                : 'Client-ID und API-Passwort aus dem MeinSHK-Verwaltungssystem (Einstellungen → Schnittstelle).'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-app-border text-app-text-subtle hover:bg-app-hover hover:text-app-text disabled:opacity-40"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto">
          {error ? <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{error}</div> : null}
          <div>
            <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
              Client-ID
              <RequiredMark />
            </label>
            <input
              ref={firstRef}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className={`${SETTINGS_INPUT_CLASS} font-mono`}
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div>
            <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>
              API-Passwort
              {hasStoredPassword ? null : <RequiredMark />}
            </label>
            <div className="relative">
              <input
                type={visible ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`${SETTINGS_INPUT_CLASS} pr-10 font-mono`}
                autoComplete="new-password"
                placeholder={hasStoredPassword ? 'Unverändert lassen' : undefined}
                spellCheck={false}
              />
              <button
                type="button"
                onClick={() => setVisible((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-app-text-muted hover:bg-app-hover"
                aria-label={visible ? 'Passwort verbergen' : 'Passwort anzeigen'}
              >
                {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-app-text-subtle">
              Hinweis: MeinSHK sperrt den Zugang nach 30 falschen Versuchen für 15 Minuten. Wir prüfen das Passwort deshalb nur einmal beim Speichern.
            </p>
          </div>
          <div className="flex justify-end pt-1">
            <button type="button" className={SETTINGS_BTN_PRIMARY} disabled={saving} onClick={() => void save()}>
              {saving ? <LoadingSpinner size="sm" /> : null}
              Speichern und prüfen
            </button>
          </div>
        </div>
      </div>
    </AppModal>
  );
}
